import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, IconButton, Tooltip, Avatar,
  Divider, Chip, useMediaQuery, AppBar, Toolbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AddCircleOutline, History as HistoryIcon, People, AdminPanelSettings, MenuBook, FileDownload,
  DarkMode, LightMode, Logout, Menu as MenuIcon, Assessment,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import { useAnalysis } from '../AnalysisContext';
import { useThemeMode } from '../ThemeModeContext';
import { SIDEBAR_WIDTH } from '../theme';
import LanguageToggle from '../LanguageToggle';
import ContactFooter from '../ContactFooter';
import ChatBot from '../ChatBot';

function NavItem({ to, icon, label, count, end, onClick, external, accent = '#a5b4fc' }) {
  const content = (
    <>
      <ListItemIcon sx={{ minWidth: 36, color: accent, transition: 'transform .25s ease', '.MuiListItemButton-root:hover &': { transform: 'scale(1.18) rotate(-6deg)' } }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
      {count !== undefined && count !== null && (
        <Chip size="small" label={count} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: `color-mix(in srgb, ${accent} 18%, transparent)`, color: accent, borderRadius: 1.5 }} />
      )}
    </>
  );
  const sx = {
    mx: 1.25, mb: 0.4, borderRadius: 2.5, color: '#cbd5e1', py: 0.95, position: 'relative', overflow: 'hidden',
    transition: 'transform .2s ease, background-color .2s ease, color .2s ease, box-shadow .2s ease',
    '&:hover': { bgcolor: 'rgba(148,163,184,0.10)', color: '#fff', transform: 'translateX(4px)' },
    '&.active': { color: '#fff', background: 'var(--sb-active)', boxShadow: 'var(--sb-active-glow)' },
    '&.active::before': { content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 3, background: 'var(--sb-active-bar)' },
  };
  if (external) return <ListItemButton component="a" href={to} onClick={onClick} sx={sx}>{content}</ListItemButton>;
  return <ListItemButton component={NavLink} to={to} end={end} onClick={onClick} sx={sx}>{content}</ListItemButton>;
}

const SectionLabel = ({ children }) => (
  <Typography sx={{ px: 3, pt: 2.5, pb: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase' }}>
    {children}
  </Typography>
);

function SidebarContent({ onNavigate }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { uploads, sharedWithMe, isAdmin, adminData, userEmail } = useAnalysis();
  const email = userEmail || user?.username || '';
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', color: '#e2e8f0', position: 'relative', overflow: 'hidden',
      background: 'var(--sb-bg)',
      '&::before': { content: '""', position: 'absolute', width: 260, height: 260, borderRadius: '50%', top: -90, left: -80, background: 'var(--sb-glow-1)', animation: 'blobMove 14s ease-in-out infinite', pointerEvents: 'none' },
      '&::after': { content: '""', position: 'absolute', width: 220, height: 220, borderRadius: '50%', bottom: 40, right: -90, background: 'var(--sb-glow-2)', animation: 'blobMove 18s ease-in-out infinite reverse', pointerEvents: 'none' } }}>
      <Box sx={{ px: 2.5, py: 2.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2.25, background: 'var(--g-brand)', backgroundSize: '200% 200%', animation: 'gradientShift 6s ease infinite, pulseRing 3s ease-out infinite', display: 'grid', placeItems: 'center', position: 'relative', zIndex: 1 }}>
          <Assessment sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.01em', color: '#fff', lineHeight: 1.1 }}>SSAT Sizing</Typography>
          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>SQL Server → Amazon RDS</Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, overflowY: 'auto', pt: 0, position: 'relative', zIndex: 1 }}>
        <SectionLabel>{t('nav.workspace', 'Workspace')}</SectionLabel>
        <NavItem accent="var(--nav-1)" to="/" end icon={<AddCircleOutline fontSize="small" />} label={t('nav.newAnalysis', 'New Analysis')} onClick={onNavigate} />
        <NavItem accent="var(--nav-2)" to="/history" icon={<HistoryIcon fontSize="small" />} label={t('nav.history', 'History')} count={uploads.length} onClick={onNavigate} />
        <NavItem accent="var(--nav-3)" to="/shared" icon={<People fontSize="small" />} label={t('nav.shared', 'Shared with me')} count={sharedWithMe.length} onClick={onNavigate} />

        {isAdmin && (
          <>
            <SectionLabel>{t('nav.admin', 'Admin')}</SectionLabel>
            <NavItem accent="var(--nav-4)" to="/admin" icon={<AdminPanelSettings fontSize="small" />} label={t('nav.customerUploads', 'Customer uploads')} count={adminData.length} onClick={onNavigate} />
          </>
        )}

        <SectionLabel>{t('nav.help', 'Help')}</SectionLabel>
        <NavItem accent="var(--nav-5)" to="/detailed-guide" icon={<MenuBook fontSize="small" />} label={t('nav.guide', 'SSAT Guide')} onClick={onNavigate} />
        <NavItem accent="var(--nav-6)" to="/SSATcollector.zip" external icon={<FileDownload fontSize="small" />} label={t('nav.collector', 'Get Collector')} onClick={onNavigate} />
      </List>

      <Divider sx={{ borderColor: 'rgba(148,163,184,0.16)' }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.25, position: 'relative', zIndex: 1 }}>
        <Avatar sx={{ width: 34, height: 34, background: 'var(--g-brand)', fontSize: 14, fontWeight: 700 }}>{(email[0] || 'U').toUpperCase()}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{email || 'Signed in'}</Typography>
          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{isAdmin ? 'Administrator' : 'User'}</Typography>
        </Box>
        <Tooltip title={t('nav.logout', 'Logout')}>
          <IconButton size="small" onClick={logout} sx={{ color: '#94a3b8', '&:hover': { color: '#fff' } }}><Logout fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const TITLES = { '/': 'nav.newAnalysis|New Analysis', '/history': 'nav.history|History', '/shared': 'nav.shared|Shared with me', '/admin': 'nav.customerUploads|Customer uploads', '/detailed-guide': 'nav.guide|SSAT Guide' };

export default function AppShell() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { mode, toggleMode } = useThemeMode();
  const { pathname } = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const { results, userEmail } = useAnalysis();
  const [key, def] = (TITLES[pathname] || 'nav.newAnalysis|New Analysis').split('|');
  const isAmazonUser = (userEmail || user?.signInDetails?.loginId || '').endsWith('@amazon.com');

  const okRes = results?.batchMode ? (results.results || []).filter(r => r.success) : [];
  const avgP95 = (k) => okRes.reduce((s, r) => s + (r.analysis?.[k]?.p95 || 0), 0) / (okRes.length || 1);
  const maxOf = (k) => Math.max(...(okRes.map(r => r.analysis?.[k]?.max || 0).concat([0])));
  const chatResults = results?.batchMode ? {
    analysis: {
      cpu: { avg: avgP95('cpu'), max: maxOf('cpu'), p95: avgP95('cpu') },
      memory: { avg: avgP95('memory'), max: maxOf('memory'), p95: avgP95('memory') },
      currentServer: { cores: okRes.length + ' servers' },
    },
    recommendations: okRes.map(r => r.recommendations?.[0]).filter(Boolean),
    batchMode: true,
    serverCount: okRes.length,
  } : results;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {isDesktop ? (
        <Drawer variant="permanent" open PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: 0 } }} sx={{ width: SIDEBAR_WIDTH, flexShrink: 0 }}>
          <SidebarContent />
        </Drawer>
      ) : (
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: 0 } }}>
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      )}

      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', backgroundImage: 'none', '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, background: 'var(--bar-line)', backgroundSize: '300% 100%', animation: 'gradientShift 9s ease infinite' } }}>
          <Toolbar sx={{ gap: 1, minHeight: 60 }}>
            {!isDesktop && <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="menu"><MenuIcon /></IconButton>}
            <Typography variant="h6" sx={{ flex: 1, fontSize: 17 }}>{t(key, def)}</Typography>
            <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
              <IconButton onClick={toggleMode} aria-label="toggle theme">{mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}</IconButton>
            </Tooltip>
            <LanguageToggle />
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, maxWidth: 1480, width: '100%', mx: 'auto' }}>
          <Box key={pathname} className="page-enter"><Outlet /></Box>
        </Box>
        <ContactFooter />
      </Box>

      {isAmazonUser && (
        <ChatBot analysisResults={chatResults} isVisible={!!results} getAuthHeaders={getAuthHeaders} userEmail={userEmail} />
      )}
    </Box>
  );
}
